// src/pages/partners/partners-logged-in/routes/client/ExerciseDashboardWeb.tsx
import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Bar,
  BarChart,
} from "recharts";
import { Workout } from "../../../../models/types";
import GlassContainer from "../../components/liquid-glass";

/** Shared */
export type DateRange = { start: Date; end: Date };
type Preset = "TODAY" | "P7" | "PMONTH" | "PYEAR" | "CUSTOM";

const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};
const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const ymd = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(x.getDate()).padStart(2, "0")}`;
};
const enumerateDaysInclusive = (start: Date, end: Date): string[] => {
  const out: string[] = [];
  const s = startOfDay(start).getTime();
  const e = startOfDay(end).getTime();
  const MS = 86_400_000;
  const days = Math.max(1, Math.floor((e - s) / MS) + 1);
  for (let i = 0; i < days; i++) out.push(ymd(new Date(s + i * MS)));
  return out;
};
const niceDateTick = (val: unknown) => {
  if (!val) return "";
  const s = String(val);
  const p = s.split("-");
  if (p.length === 3) return `${Number(p[1])}/${Number(p[2])}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : `${d.getMonth() + 1}/${d.getDate()}`;
};

function buildDailyWorkoutMap(
  workouts: Workout[],
  start: Date,
  end: Date
): Record<
  string,
  {
    hasWorkout: boolean;
    duration: number;
    volume: number;
    muscleGroups: Record<string, number>;
  }
> {
  const days = enumerateDaysInclusive(start, end);
  const map: Record<
    string,
    {
      hasWorkout: boolean;
      duration: number;
      volume: number;
      muscleGroups: Record<string, number>;
    }
  > = {};
  for (const d of days)
    map[d] = { hasWorkout: false, duration: 0, volume: 0, muscleGroups: {} };

  for (const w of workouts ?? []) {
    const key = ymd(new Date(w.dayOfExecution));
    if (!map[key]) continue;
    map[key].hasWorkout = true;
    map[key].duration += Number(w.durationInMinutes ?? 0);
    map[key].volume += Number(w.totalVolume ?? 0);
    const mgs: string[] =
      w.template?.exercises
        ?.map((e: any) => e.exercise?.muscleGroup)
        ?.filter(Boolean) ?? [];
    for (const g of mgs)
      map[key].muscleGroups[g] = (map[key].muscleGroups[g] ?? 0) + 1;
  }
  return map;
}

function dailySeriesFromMap(m: ReturnType<typeof buildDailyWorkoutMap>) {
  return Object.keys(m).map((d) => ({
    date: d,
    duration: m[d].duration,
    volume: m[d].volume,
    hasWorkout: m[d].hasWorkout,
  }));
}

function topMuscleGroups(m: ReturnType<typeof buildDailyWorkoutMap>, topN = 8) {
  const counter: Record<string, number> = {};
  for (const d of Object.values(m)) {
    for (const [k, v] of Object.entries(d.muscleGroups)) {
      counter[k] = (counter[k] ?? 0) + v;
    }
  }
  return Object.entries(counter)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([name, count]) => ({ name, count }));
}

const ChartTooltip = ({
  active,
  payload,
  label,
  title,
  unit,
}: {
  active?: boolean;
  payload?: any[];
  label?: any;
  title: string;
  unit?: string;
}) => {
  if (!active || !payload || !payload.length) return null;
  const val = payload[0]?.value ?? 0;
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRadius: 1.5,
        p: 1,
      }}
    >
      <Typography variant="caption">{niceDateTick(label)}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {title}: {Math.round(val)}
        {unit ?? ""}
      </Typography>
    </Box>
  );
};

export default function ExerciseDashboardWeb({
  workouts,
  range,
  showControls = false,
  onRangeChange,
}: {
  workouts: Workout[];
  /** Unified range coming from Client */
  range: DateRange;
  /** Optional: if true, shows local chips/dialog and emits onRangeChange */
  showControls?: boolean;
  onRangeChange?: (start: Date, end: Date) => void;
}) {
  // Local state only used when controls are shown
  const [customOpen, setCustomOpen] = React.useState(false);
  const [customStart, setCustomStart] = React.useState<string>(
    ymd(range.start)
  );
  const [customEnd, setCustomEnd] = React.useState<string>(ymd(range.end));

  const emitPreset = (p: Preset) => {
    if (!onRangeChange) return;
    const now = new Date();
    switch (p) {
      case "TODAY":
        return onRangeChange(startOfDay(now), endOfDay(now));
      case "P7":
        return onRangeChange(startOfDay(addDays(now, -6)), endOfDay(now));
      case "PMONTH":
        return onRangeChange(startOfDay(addDays(now, -29)), endOfDay(now));
      case "PYEAR":
        return onRangeChange(startOfDay(addDays(now, -364)), endOfDay(now));
      case "CUSTOM":
        setCustomStart(ymd(range.start));
        setCustomEnd(ymd(range.end));
        setCustomOpen(true);
    }
  };

  const applyCustom = () => {
    if (!onRangeChange) return setCustomOpen(false);
    const s = new Date(customStart);
    const e = new Date(customEnd);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) {
      setCustomOpen(false);
      return;
    }
    const start = s.getTime() <= e.getTime() ? s : e;
    const end = s.getTime() <= e.getTime() ? e : s;
    onRangeChange(startOfDay(start), endOfDay(end));
    setCustomOpen(false);
  };

  const map = React.useMemo(
    () => buildDailyWorkoutMap(workouts ?? [], range.start, range.end),
    [workouts, range.start.getTime(), range.end.getTime()]
  );
  const series = React.useMemo(() => dailySeriesFromMap(map), [map]);
  const muscles = React.useMemo(() => topMuscleGroups(map), [map]);

  return (
    <Box>
      {showControls && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 4,
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            background: "transparent",
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              {range.start.toLocaleDateString()} –{" "}
              {range.end.toLocaleDateString()}
            </Typography>

            <Chip
              size="small"
              label="Today"
              variant="outlined"
              onClick={() => emitPreset("TODAY")}
            />
            <Chip
              size="small"
              label="Past 7 days"
              variant="outlined"
              onClick={() => emitPreset("P7")}
            />
            <Chip
              size="small"
              label="Past month"
              variant="outlined"
              onClick={() => emitPreset("PMONTH")}
            />
            <Chip
              size="small"
              label="Past year"
              variant="outlined"
              onClick={() => emitPreset("PYEAR")}
            />
            <Chip
              size="small"
              label="Custom…"
              variant="outlined"
              onClick={() => emitPreset("CUSTOM")}
            />
          </Box>
        </Paper>
      )}

      {/* Charts */}
      <Grid container spacing={2} sx={{ mt: 0 }}>
        {/* 1) Durations */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
              backgroundColor: "transparent",
            }}
          >
            <GlassContainer>
              <CardContent sx={{ p: 2.5, backgroundColor: "transparent" }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Workout Durations
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={series}
                      margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={niceDateTick} />
                      <YAxis width={40} allowDecimals={false} />
                      <Tooltip
                        content={({ active, payload, label }) => (
                          <ChartTooltip
                            active={active}
                            payload={payload}
                            label={label}
                            title="Duration"
                            unit=" min"
                          />
                        )}
                      />
                      <Line
                        type="monotone"
                        dataKey="duration"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </GlassContainer>
          </Card>
        </Grid>

        {/* 2) Volume */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
              backgroundColor: "transparent",
            }}
          >
            <GlassContainer>
              <CardContent sx={{ p: 2.5, backgroundColor: "transparent" }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Total Volume Over Time
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={series}
                      margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={niceDateTick} />
                      <YAxis width={40} allowDecimals={false} />
                      <Tooltip
                        content={({ active, payload, label }) => (
                          <ChartTooltip
                            active={active}
                            payload={payload}
                            label={label}
                            title="Volume"
                          />
                        )}
                      />
                      <Line
                        type="monotone"
                        dataKey="volume"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </GlassContainer>
          </Card>
        </Grid>

        {/* 3) Muscle groups */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
              backgroundColor: "transparent",
            }}
          >
            <GlassContainer>
              <CardContent sx={{ p: 2.5, backgroundColor: "transparent" }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Workouts by Muscle Group
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={muscles}
                      margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        interval={0}
                        angle={-20}
                        textAnchor="end"
                        height={48}
                      />
                      <YAxis width={32} allowDecimals={false} />
                      <Tooltip
                        content={({ active, payload, label }) => (
                          <Box
                            sx={{
                              bgcolor: "background.paper",
                              border: (t) => `1px solid ${t.palette.divider}`,
                              borderRadius: 1.5,
                              p: 1,
                            }}
                          >
                            <Typography variant="caption">{label}</Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              Sessions: {payload?.[0]?.value ?? 0}
                            </Typography>
                          </Box>
                        )}
                      />
                      <Bar dataKey="count" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </GlassContainer>
          </Card>
        </Grid>
      </Grid>

      {/* Optional local controls dialog */}
      {showControls && (
        <Dialog open={customOpen} onClose={() => setCustomOpen(false)}>
          <DialogTitle>Select custom range</DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
              <TextField
                label="Start"
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="End"
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCustomOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button variant="contained" onClick={applyCustom}>
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
